/* @flow */
// typecheck: ignore file
import Audio from 'components/Audio.jsx';
import Avatar from 'components/Avatar.jsx';
import cx from 'classnames';
import ChatMessage from 'entities/ChatMessage';
import ChatMessageStore from 'chat/stores/ChatMessageStore';
import ChatMessageActions from '../actions/ChatMessageActions';
import ChatReadReceipt from 'chat/components/ChatReadReceipt.jsx';
import { Container } from 'flux/utils';
import CurrentUser from 'auth/CurrentUser';
import FeatureStore from 'stores/FeatureStore';
import Glyph from 'components/Glyph.jsx';
import Link from 'components/Link.jsx';
import MessageItemContent from './MessageItemContent.jsx';
import React from 'react';
import TooltippedReactionButton from 'chat/components/TooltippedReactionButton.jsx';
import ReactionDisplay from 'chat/components/ReactionDisplay.jsx';
import ReactionSummary from 'entities/ReactionSummary';
import Time from 'components/Time.jsx';

import './styles/MessageItem.scss';

type Props = {
  message: ChatMessage,
  failed: boolean,
  sentBySelf: boolean,
  senderColor: string,
  senderInitials: string,
  lastOfGroup: boolean,
};

type State = {
  hovered: boolean,
  reactionPickerVisible: boolean,
  reactionSummary: ?ReactionSummary,
}

class MessageItem extends React.Component {
  props: Props;

  state: State = {
    reactionSummary: null,
    hovered: false,
    reactionPickerVisible: false,
  };

  static getStores() {
    return [ChatMessageStore];
  }

  static calculateState(prevState: State, props: Props): State {
    const chatMessages = ChatMessageStore.getChatMessages(props.message.getChatId());
    const chatMessage = chatMessages.find(m => m.id === props.message.id);
    const reactionSummary = chatMessage.getReactionSummary();
    return {
      ...prevState,
      reactionSummary,
    };
  }

  handleCancel = () => {
    ChatMessageActions.removeMessage(this.props.message);
  };

  handleRetry = () => {
    ChatMessageActions.sendMessage(this.props.message);
  };

  showReadReceipt(message: ChatMessage) {
    return message.getSender().id === CurrentUser.getUser().id;
  }

  onHoverReactionPickerShow = () => {
    this.setState({ reactionPickerVisible: true });
  };

  onHoverReactionPickerHide = () => {
    this.setState({ reactionPickerVisible: false });
  };

  onEnterHover = () => {
    this.setState({ hovered: true });
  };

  onLeaveHover = () => {
    this.setState({ hovered: false });
  };

  renderReadReceipt(message: ChatMessage) {
    return message.hasReactions() ?
      null :
      (
      <div className="MessageItem-readIcon">
        {
          this.showReadReceipt(message) &&
            <ChatReadReceipt hovered labelDisplay glyphDisplay message={message} />
        }
      </div>
      );
  }

  renderReactions() {
    const { message, sentBySelf } = this.props;
    return (
      <ReactionDisplay
        message={message}
        reactionSummary={this.state.reactionSummary}
        sentBySelf={sentBySelf}
      />
    );
  }

  renderRetry() {
    return (
      <div className="MessageItem-retry">
        <Glyph name="caution" className="MessageItem-retryGlyph" />
        Message failed to send.
        {' '}
        <Link weight="semibold" onClick={this.handleRetry}>Retry</Link>
        {' '}
        or
        {' '}
        <Link weight="semibold" onClick={this.handleCancel}>Cancel</Link>
      </div>
    );
  }

  renderHover() {
    const { message, sentBySelf } = this.props;
    const reactionsEnabled = FeatureStore.hasFeature('web.reactions');

    return (
      <div className="MessageItem-hover">
        <div
          className={cx({
            'MessageItem-hover-receipt': !message.hasReactions(),
          })}>
          <Time
            date={message.getSentAt()}
            hour="numeric"
            minute="2-digit"
          />
          {
            sentBySelf &&
              <ChatReadReceipt hovered labelDisplay={false} glyphDisplay message={message} />
          }
        </div>
        <div className="MessageItem-hover-reactionButton">
          {reactionsEnabled && !message.hasReactions() &&
            <TooltippedReactionButton
              message={message}
              onShow={this.onHoverReactionPickerShow}
              onHide={this.onHoverReactionPickerHide}
              reactionPickerVisible={this.state.reactionPickerVisible}
            />
          }
        </div>
      </div>
    );
  }

  renderDroplet() {
    const { senderColor, senderInitials, lastOfGroup, sentBySelf } = this.props;
    return (lastOfGroup && !sentBySelf) ?
      (
        <span className="MessageItem-droplet">
          <Avatar
            colorHex={senderColor}
            size="small"
            text={senderInitials}
          />
        </span>
      ) : null;
  }

  render() {
    const { message, failed, sentBySelf } = this.props;
    const { hovered, reactionPickerVisible } = this.state;
    const reactionsEnabled = FeatureStore.hasFeature('web.reactions');

    const hasBody = !!message.getBody();
    const file = message.getFile();
    const isSoloAudioFile = !hasBody && file && Audio.isSupportedType(file.getContentType());

    return (
      <div
        className={cx('MessageItem', {
          'MessageItem-self': sentBySelf,
          'MessageItem-other': !sentBySelf,
          'MessageItem-noRoundedCorners': isSoloAudioFile,
          'MessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="MessageItem-row"
          onMouseEnter={this.onEnterHover}
          onMouseLeave={this.onLeaveHover}>
          {this.renderHover()}
          <div className="MessageItem-container">
            <MessageItemContent message={message} failed={failed} sentBySelf={sentBySelf} />
            {failed && this.renderRetry()}
          </div>
          {this.renderDroplet()}
        </div>
        {reactionsEnabled && this.renderReactions()}
        {this.renderReadReceipt(message)}
      </div>
    );
  }
}

export default Container.create(MessageItem, { withProps: true });



// WEBPACK FOOTER //
// ./src/chat/components/MessageItem.jsx